import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { deleteChannel } from '../api/client'
import type { ChannelConfig } from '../types'
import styles from './AdminDeleteChannels.module.css'

type ChannelResult =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'success' }
  | { status: 'error'; message: string }

interface Props {
  channels: ChannelConfig[]
  sessionToken: string
}

export function AdminDeleteChannels({ channels, sessionToken }: Props) {
  const navigate = useNavigate()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [results, setResults] = useState<Record<string, ChannelResult>>({})
  const [running, setRunning] = useState(false)

  const hasRun = Object.keys(results).length > 0

  function toggleSelect(channelId: string) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(channelId) ? next.delete(channelId) : next.add(channelId)
      return next
    })
  }

  function toggleAll() {
    if (selected.size === channels.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(channels.map(c => c.channelId)))
    }
  }

  async function runDelete() {
    if (selected.size === 0) return
    setRunning(true)

    // Mark all selected as pending
    const initial: Record<string, ChannelResult> = {}
    selected.forEach(id => { initial[id] = { status: 'pending' } })
    setResults(initial)

    // Delete all in parallel
    await Promise.all([...selected].map(async channelId => {
      try {
        await deleteChannel(sessionToken, channelId)
        setResults(prev => ({ ...prev, [channelId]: { status: 'success' } }))
      } catch (err: unknown) {
        const e = err as { status?: number; message?: string }
        const message = e?.status === 403
          ? 'Admin access required'
          : (e?.message ?? 'Delete failed')
        setResults(prev => ({ ...prev, [channelId]: { status: 'error', message } }))
      }
    }))

    setRunning(false)
  }

  const successCount = Object.values(results).filter(r => r.status === 'success').length
  const errorCount   = Object.values(results).filter(r => r.status === 'error').length

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/')}>
          <Icon name="arrow_back" size="sm" /> Channels
        </button>
        <div className={styles.headerTitle}>Delete Channels</div>
        <div className={styles.headerSub}>Select channels to permanently remove. This cannot be undone.</div>
      </div>

      {!hasRun && (
        <div className={styles.toolbar}>
          <button className={styles.selectAll} onClick={toggleAll}>
            {selected.size === channels.length ? 'Deselect all' : 'Select all'}
          </button>
          <span className={styles.selectedCount}>
            {selected.size > 0 ? `${selected.size} selected` : 'None selected'}
          </span>
          <button
            className={styles.deleteBtn}
            onClick={runDelete}
            disabled={selected.size === 0 || running}
          >
            <Icon name="delete_forever" size="sm" />
            Delete {selected.size > 0 ? `${selected.size} channel${selected.size > 1 ? 's' : ''}` : 'selected'}
          </button>
        </div>
      )}

      {hasRun && (
        <div className={styles.summary}>
          {successCount > 0 && (
            <span className={styles.summarySuccess}>
              <Icon name="check_circle" size="sm" /> {successCount} deleted
            </span>
          )}
          {errorCount > 0 && (
            <span className={styles.summaryError}>
              <Icon name="error_outline" size="sm" /> {errorCount} failed
            </span>
          )}
        </div>
      )}

      <div className={styles.list}>
        {channels.map(ch => {
          const result = results[ch.channelId]
          const isSelected = selected.has(ch.channelId)
          const isPending = result?.status === 'pending'
          const isSuccess = result?.status === 'success'
          const isError = result?.status === 'error'

          if (hasRun && !result) return null // not selected, hide after run

          return (
            <div
              key={ch.channelId}
              className={`${styles.row}
                ${isSelected && !hasRun ? styles.rowSelected : ''}
                ${isSuccess ? styles.rowSuccess : ''}
                ${isError ? styles.rowError : ''}
              `}
              onClick={() => !hasRun && toggleSelect(ch.channelId)}
            >
              {!hasRun && (
                <div className={`${styles.checkbox} ${isSelected ? styles.checkboxChecked : ''}`}>
                  {isSelected && <Icon name="check" size={13} />}
                </div>
              )}

              {isPending && <Icon name="hourglass_empty" size="sm" className={styles.statusIcon} />}
              {isSuccess && <Icon name="check_circle" size="sm" className={styles.statusIconSuccess} />}
              {isError   && <Icon name="error_outline" size="sm" className={styles.statusIconError} />}

              <div className={styles.rowBody}>
                <div className={styles.rowName}>{ch.channelName}</div>
                {isSuccess && <div className={styles.rowResult}>Successfully deleted</div>}
                {isError   && <div className={styles.rowResultError}>{(result as { message: string }).message}</div>}
                {!hasRun && ch.prefix && <div className={styles.rowMeta}><code>{ch.prefix}</code></div>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
